import React from "react";

/**
 * Colors and words a theme can change on the Collections, Category, and
 * Product pages, beyond each page's main settings.
 *
 * A color lists the parts it paints as [selector, CSS property]. "{scope}" is
 * the page's root ([data-theme-group="…"]). Only colors the merchant set are
 * written, so an empty field keeps the theme's own look.
 *
 * A text has a short `name` used by the page and its original wording as the
 * fallback. Add a color or a word here and the editor shows a field for it.
 */

const S = "{scope}";
const PAGE = ".main-content:has({scope})";

export const PRODUCT_COLORS = [
  { key: "productPageBackground", label: "Page background", rules: [[PAGE, "background-color"]] },
  { key: "productCrumbBackground", label: "Breadcrumb bar background", rules: [[`${PAGE} .page-header-modern`, "background-color"]] },
  { key: "productCrumbText", label: "Breadcrumb text", rules: [[`${PAGE} .breadcrumb-modern`, "color"], [`${PAGE} .breadcrumb-modern a`, "color"]] },
  { key: "productGalleryBackground", label: "Photo background", rules: [[`${S} .product-showcase-card`, "background"]] },
  { key: "productThumbBorder", label: "Selected small photo outline", rules: [[`${S} .multi-thumb-item.active`, "border-color"]] },
  { key: "productBoxBackground", label: "Details box background", rules: [[`${S} .product-info-inner`, "background-color"]] },
  { key: "productCategoryColor", label: "Category name", rules: [[`${S} .product-category-tag`, "color"]] },
  { key: "productTitleColor", label: "Product name", rules: [[`${S} .product-view-title`, "color"]] },
  { key: "productOldPriceColor", label: "Crossed-out price", rules: [[`${S} .original-price-hero`, "color"]] },
  { key: "productTextColor", label: "Body text", rules: [[`${S} .product-detail-copy`, "color"], [`${S} .product-spec-tabs p`, "color"]] },
  { key: "productHeadingColor", label: "Headings (description, reviews, recently viewed)", rules: [[`${S} .product-spec-tabs h6`, "color"], [`${S} .product-reviews h4`, "color"], [`${S} .product-recent h4`, "color"]] },
  { key: "productStockInColor", label: "In stock text", rules: [[`${S} .stock-pill.in-stock`, "color"], [`${S} .stock-pill.in-stock .stock-dot`, "background-color"]] },
  { key: "productStockOutColor", label: "Out of stock text", rules: [[`${S} .stock-pill.out-of-stock`, "color"], [`${S} .stock-pill.out-of-stock .stock-dot`, "background-color"]] },
  { key: "productAddBackground", label: "Add to cart button", rules: [[`${S} .btn-add-to-cart`, "background-color"], [`${S} .btn-add-to-cart`, "border-color"]] },
  { key: "productAddText", label: "Add to cart button text", rules: [[`${S} .btn-add-to-cart`, "color"]] },
  { key: "productBuyBackground", label: "Buy Now button", rules: [[`${S} .btn-buy-now`, "background-color"], [`${S} .btn-buy-now`, "border-color"]] },
  { key: "productBuyText", label: "Buy Now button text", rules: [[`${S} .btn-buy-now`, "color"]] },
  { key: "productTrustBackground", label: "Delivery strip background", rules: [[`${S} .product-trust-strip`, "background-color"]] },
  { key: "productTrustText", label: "Delivery strip text", rules: [[`${S} .product-trust-strip span`, "color"]] },
  { key: "productTrustIcon", label: "Delivery strip icons", rules: [[`${S} .product-trust-strip svg`, "color"]] },
  { key: "productReviewsBackground", label: "Reviews box background", rules: [[`${S} .product-reviews`, "background-color"]] },
];

export const PRODUCT_TEXTS = [
  { key: "productTextInStock", name: "inStock", label: "In stock", fallback: "In Stock" },
  { key: "productTextUnitsReady", name: "unitsReady", label: "After the stock count", fallback: "units ready to ship" },
  { key: "productTextOutOfStock", name: "outOfStock", label: "Out of stock", fallback: "Out of Stock" },
  { key: "productTextUnavailable", name: "unavailable", label: "Button when sold out", fallback: "Item Currently Unavailable" },
  { key: "productTextFeatured", name: "featured", label: "Featured badge", fallback: "Featured Product" },
  { key: "productTextTrending", name: "trending", label: "Trending badge", fallback: "Trending / Popular" },
  { key: "productTextReviewsWord", name: "reviewsWord", label: "After the review count", fallback: "customer reviews" },
  { key: "productTextFreeShipping", name: "freeShipping", label: "Free delivery (no minimum)", fallback: "Free Shipping" },
  { key: "productTextFreeOver", name: "freeOver", label: "Free delivery over an amount", fallback: "Free Over" },
  { key: "productTextReviewsTitle", name: "reviewsTitle", label: "Reviews heading", fallback: "Customer Reviews & Ratings" },
  { key: "productTextReviewsSubtitle", name: "reviewsSubtitle", label: "Reviews subheading", fallback: "Verified feedback from real customers" },
  { key: "productTextWriteReview", name: "writeReview", label: "Write a review button", fallback: "Write a Review" },
  { key: "productTextNoReviews", name: "noReviews", label: "When there are no reviews", fallback: "No reviews yet. Be the first to review this product!" },
  { key: "productTextRecentTitle", name: "recentTitle", label: "Recently viewed heading", fallback: "Recently Viewed Products" },
  { key: "productTextFbtTitle", name: "fbtTitle", label: "Frequently bought together heading", fallback: "Frequently bought together" },
  { key: "productTextBundleTitle", name: "bundleTitle", label: "Bundle heading", fallback: "Bundle and save" },
  { key: "productTextAddSelected", name: "addSelected", label: "Add selected button", fallback: "Add selected to cart" },
  { key: "productTextAddBundle", name: "addBundle", label: "Add bundle button", fallback: "Add bundle to cart" },
  { key: "productTextChooseOptions", name: "chooseOptions", label: "Choose options link", fallback: "Choose options" },
  { key: "productTextYouSave", name: "youSave", label: "Savings label", fallback: "You save" },
  { key: "productTextThisItem", name: "thisItem", label: "Label for the product being viewed", fallback: "This item" },
];

export const CATEGORY_COLORS = [
  { key: "collectionCrumbColor", label: "Breadcrumb text", rules: [[`${S} .shop-crumbs`, "color"], [`${S} .shop-crumbs a`, "color"]] },
  { key: "collectionDescriptionColor", label: "Category description", rules: [[`${S} .shop-collection-desc p`, "color"]] },
  { key: "collectionFilterColor", label: "Filter button", rules: [[`${S} .shop-filter-btn`, "color"], [`${S} .shop-filter-btn`, "border-color"]] },
  { key: "collectionCountColor", label: "Product count", rules: [[`${S} .shop-count`, "color"]] },
  { key: "collectionChipBackground", label: "Active filter chips", rules: [[`${S} .shop-chip`, "background-color"]] },
  { key: "collectionChipText", label: "Active filter chip text", rules: [[`${S} .shop-chip`, "color"]] },
  { key: "collectionPageCurrent", label: "Current page number", rules: [[`${S} .shop-pages .is-current`, "background-color"], [`${S} .shop-pages .is-current`, "border-color"]] },
  { key: "collectionPageCurrentText", label: "Current page number text", rules: [[`${S} .shop-pages .is-current`, "color"]] },
  { key: "collectionLoadMoreBackground", label: "Load more button", rules: [[`${S} .shop-load`, "background-color"], [`${S} .shop-load`, "border-color"]] },
  { key: "collectionLoadMoreText", label: "Load more button text", rules: [[`${S} .shop-load`, "color"]] },
];

export const CATEGORY_TEXTS = [
  { key: "collectionTextFilter", name: "filter", label: "Filter button", fallback: "Filter" },
  { key: "collectionTextSortBy", name: "sortBy", label: "Sort label", fallback: "Sort by" },
  { key: "collectionTextProduct", name: "product", label: "Count word (one)", fallback: "product" },
  { key: "collectionTextProducts", name: "products", label: "Count word (many)", fallback: "products" },
  { key: "collectionTextAvailability", name: "availability", label: "Availability filter", fallback: "Availability" },
  { key: "collectionTextInStock", name: "inStock", label: "In stock filter", fallback: "In stock" },
  { key: "collectionTextOutOfStock", name: "outOfStock", label: "Out of stock filter", fallback: "Out of stock" },
  { key: "collectionTextPrice", name: "price", label: "Price filter", fallback: "Price" },
  { key: "collectionTextBrand", name: "brand", label: "Brand filter", fallback: "Brand" },
  { key: "collectionTextClearAll", name: "clearAll", label: "Clear all filters", fallback: "Clear all" },
  { key: "collectionTextLoadMore", name: "loadMore", label: "Load more button", fallback: "Load more" },
  { key: "collectionTextEmptyTitle", name: "emptyTitle", label: "No matching products heading", fallback: "No products found" },
  { key: "collectionTextEmptyText", name: "emptyText", label: "No matching products text", fallback: "Use fewer filters or clear all." },
  { key: "collectionTextNoProducts", name: "noProducts", label: "Empty category heading", fallback: "No products in this collection" },
];

export const COLLECTIONS_COLORS = [
  { key: "collectionIndexCrumbColor", label: "Breadcrumb text", rules: [[`${S} .shop-crumbs`, "color"], [`${S} .shop-crumbs a`, "color"]] },
  { key: "collectionIndexCountColor", label: "Collection count under the title", rules: [[`${S} .shop-collection-head p`, "color"]] },
  { key: "collectionIndexFallbackBackground", label: "Card without a photo", rules: [[`${S} .shop-index-fallback`, "background-color"]] },
  { key: "collectionIndexPageCurrent", label: "Current page number", rules: [[`${S} .shop-pages .is-current`, "background-color"], [`${S} .shop-pages .is-current`, "border-color"]] },
  { key: "collectionIndexPageCurrentText", label: "Current page number text", rules: [[`${S} .shop-pages .is-current`, "color"]] },
  { key: "collectionIndexLoadMoreBackground", label: "Load more button", rules: [[`${S} .shop-load`, "background-color"], [`${S} .shop-load`, "border-color"]] },
  { key: "collectionIndexLoadMoreText", label: "Load more button text", rules: [[`${S} .shop-load`, "color"]] },
];

export const COLLECTIONS_TEXTS = [
  { key: "collectionIndexTextTitle", name: "title", label: "Page title", fallback: "Collections" },
  { key: "collectionIndexTextCollection", name: "collection", label: "Count word (one)", fallback: "collection" },
  { key: "collectionIndexTextCollections", name: "collections", label: "Count word (many)", fallback: "collections" },
  { key: "collectionIndexTextProduct", name: "product", label: "Card count word (one)", fallback: "product" },
  { key: "collectionIndexTextProducts", name: "products", label: "Card count word (many)", fallback: "products" },
  { key: "collectionIndexTextLoadMore", name: "loadMore", label: "Load more button", fallback: "Load more" },
  { key: "collectionIndexTextEmptyTitle", name: "emptyTitle", label: "No collections heading", fallback: "No collections yet" },
  { key: "collectionIndexTextEmptyText", name: "emptyText", label: "No collections text", fallback: "Please check back later as new products are being added." },
];

// Only real colors are written into the page (no other CSS can get in).
const COLOR = /^(#[0-9a-fA-F]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%deg]+\)|transparent)$/;

export function pageColorCss(scope, colors, settings = {}) {
  return colors.map((color) => {
    const value = String(settings[color.key] || "").trim();
    if (!value || !COLOR.test(value)) return "";
    return color.rules
      .map(([selector, property]) => `${selector.split("{scope}").join(scope)}{${property}:${value} !important}`)
      .join("\n");
  }).filter(Boolean).join("\n");
}

/** A <style> with the merchant's colors for one page; nothing when none are set. */
export function PageColors({ scope, colors, settings }) {
  const css = pageColorCss(scope, colors, settings);
  return css ? <style data-page-colors={scope}>{css}</style> : null;
}

/** { name: text } with each text's original wording where the theme has none. */
export function pageWords(texts, settings = {}) {
  return texts.reduce((words, item) => {
    const value = typeof settings[item.key] === "string" ? settings[item.key].trim() : "";
    words[item.name] = value || item.fallback;
    return words;
  }, {});
}

export const colorFields = (colors) => colors.map((color) => ({
  key: color.key,
  label: color.label,
  type: "color",
  hint: "Leave empty to keep the theme's color.",
}));

export const textFields = (texts) => texts.map((item) => ({
  key: item.key,
  label: item.label,
  type: "text",
  placeholder: item.fallback,
}));

export const PRODUCT_SCOPE = '[data-theme-group="product-page"]';
export const CATEGORY_SCOPE = '[data-theme-group="collection-page"]';
export const COLLECTIONS_SCOPE = '[data-theme-group="collections-page"]';
